import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ZonesService } from '../services/zones.service';
import type { ProjectZone, ZoneFilters } from '../types/zones.types';
import { useState } from 'react';

export function useZones(companyId: string, initialFilters: ZoneFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ZoneFilters>({
    page: 1,
    pageSize: 10,
    status: 'all',
    type: 'all',
    ...initialFilters
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['maintenance-zones', companyId, filters],
    queryFn: () => ZonesService.getZones(companyId, filters),
    enabled: !!companyId,
  });

  const { data: stats } = useQuery({
    queryKey: ['maintenance-zones', 'stats', companyId],
    queryFn: () => ZonesService.getZoneStats(companyId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<ProjectZone>) => ZonesService.createZone(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-zones'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<ProjectZone> }) => 
      ZonesService.updateZone(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-zones'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-zones', 'detail', variables.id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ZonesService.deleteZone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-zones'] });
    },
  });

  return {
    zones: data?.data || [],
    total: data?.total || 0,
    stats: stats || { total: 0, active: 0, totalArea: 0, byType: {} as any },
    isLoading,
    error,
    filters,
    setFilters,
    createZone: createMutation.mutateAsync,
    updateZone: updateMutation.mutateAsync,
    deleteZone: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

export function useZoneDetail(id?: string) {
  return useQuery({
    queryKey: ['maintenance-zones', 'detail', id],
    queryFn: () => ZonesService.getZoneDetail(id!),
    enabled: !!id,
  });
}

export function useUpdateZone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<ProjectZone> }) => ZonesService.updateZone(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-zones'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-zones', 'detail', variables.id] });
    },
  });
}
